import { useCallback, useEffect, useId, useRef } from "react";
import type { ChangeEvent } from "react";
import { Textarea } from "@/components/ui/Select";

const MAX_AUTO_GROW_ROWS = 30;
const DEFAULT_VISIBLE_ROWS = 8;

/**
 * Long-form answer textarea used by TextArea question types on
 * /reports/generate Step 2. Renders 8 visible rows by default and
 * auto-grows with the user's content up to 30 rows before becoming
 * scrollable. A subtle character count sits below the input so users
 * filling out narrative findings or strategic recommendations have a
 * sense of how much they've written.
 */
interface LongFormTextareaProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function LongFormTextarea({
  value,
  onChange,
  disabled,
  placeholder,
}: LongFormTextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const countId = useId();

  // Resize on every value change so paste / programmatic updates also
  // grow the textarea, not just keystrokes. We measure once with the
  // height collapsed so scrollHeight reflects the actual content.
  const autosize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 24;
    const maxHeight = lineHeight * MAX_AUTO_GROW_ROWS;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    autosize();
  }, [value, autosize]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  return (
    <div>
      <Textarea
        ref={ref}
        rows={DEFAULT_VISIBLE_ROWS}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        aria-describedby={countId}
        className="resize-none"
      />
      <div
        id={countId}
        className="text-caption text-gray-mid text-right mt-space-1"
        aria-live="polite"
      >
        {value.length.toLocaleString()} character
        {value.length === 1 ? "" : "s"}
      </div>
    </div>
  );
}
